/* eslint-disable no-console */ function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var __generator = this && this.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
import { getCurrentPage } from "@kaggle/core";
import { analytics } from "@kaggle/protos";
import jwt_decode from "jwt-decode";
import { v4 as uuid } from "uuid";
import { usersClient } from "../generated/users";
import { getSessionClaims } from "../sessionClaims";
import { consoleFlushEventQueue, prodFlushEventQueue } from "./flushEventQueue";
// We test that this isn't "production" to allow tests to run with the debug client (isn't specified in tests).
var IS_DEV = process.env.NODE_ENV !== "production";
// TODO(cochamberlain): This should never being called here. The caller of makeLogger must be refactored.
var IS_JUPYTER = location.origin && (location.origin.includes("kkb-") || location.origin.includes("kaggleusercontent.com"));
/** Use to stub out things on both development and Jupyter frame. */ var USE_STUB = IS_DEV || IS_JUPYTER;
/** Interval between uploading queued events to analytics service in millis. */ var PUBLISH_EVENT_INTERVAL_MS = 15000; // 15 seconds.
// Coalesce the userId from JWT token claims.
var sessionClaims = getSessionClaims();
var userId = sessionClaims ? sessionClaims.uid : undefined;
var pageview = uuid();
var sessionInfo = {
    userId: userId,
    pageview: pageview
};
/**
 * Helper function used to create a `Logger` instance.
 *
 * @param flushEventQueue Method used to periodically process events
 *      in event queue.
 * @param fetchToken Function used to fetch the analytics token to be used.
 */ var createLogger = function(flushEventQueue, fetchToken) {
    var queue = [];
    var token = null;
    var timeoutId;
    /** Cancel polling reporters flush. */ var cancelPoll = function() {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }
    };
    /** Check if the token exists and whether it has already expired. */ var hasValidToken = function() {
        if (token === null) {
            return false;
        }
        // If we are using stubs, the token won't be something valid anyways so skip expiration check.
        if (USE_STUB) {
            return true;
        }
        // To prevent unnecessary back and forth we will try to detect if the JWT token is valid or expired (15 minute expiry).
        // To prevent issues, odd cases will warn on console and pretend things are good, allowing old logic to handle.
        try {
            var decoded = jwt_decode(token);
            if (!decoded) {
                console.warn("Could not decode analytics token. Ignoring...");
                return true;
            }
            if (!decoded.exp) {
                console.warn("Decoded analytics token contained no exp property. Ignoring...");
                return true;
            }
            // Check if exp claim is in the future.
            if (decoded.exp < (new Date().getTime() + 1) / 1000) {
                return false;
            }
        } catch (err) {
            console.warn("An error occurred decoding analytics token. Ignoring...\n", err.message);
        }
        return true;
    };
    /** Calling this continually flushes events and polling interval until cancelPoll is called. */ var flushEventsPoll = function() {
        var _ref = _asyncToGenerator(function() {
            var pageUnloading, events, status;
            var _arguments = arguments;
            return __generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        pageUnloading = _arguments.length > 0 && _arguments[0] !== void 0 ? _arguments[0] : false;
                        cancelPoll();
                        if (queue.length === 0) {
                            // Return early if queue is empty. Avoid refreshing the token in this case as it's
                            // not going to be used anyways.
                            timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);
                            return [
                                2
                            ];
                        }
                        if (!!hasValidToken()) return [
                            3,
                            2
                        ];
                        token = getAnalyticsTokenFromLocalStorage();
                        if (!!hasValidToken()) return [
                            3,
                            2
                        ];
                        return [
                            4,
                            fetchToken()
                        ];
                    case 1:
                        token = _state.sent();
                        saveAnalyticsTokenToLocalStorage(token);
                        _state.label = 2;
                    case 2:
                        if (!(token !== null)) return [
                            3,
                            4
                        ];
                        events = queue.splice(0);
                        return [
                            4,
                            flushEventQueue(events, sessionInfo, token, pageUnloading)
                        ];
                    case 3:
                        status = _state.sent();
                        if (status === 401) {
                            // If received unauthorized, token may have expired. Re-fetch token on next round.
                            token = null;
                        }
                        _state.label = 4;
                    case 4:
                        timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);
                        return [
                            2
                        ];
                }
            });
        });
        return function flushEventsPoll() {
            return _ref.apply(this, arguments);
        };
    }();
    // Schedule event flushing to start polling at correct interval.
    timeoutId = window.setTimeout(flushEventsPoll, PUBLISH_EVENT_INTERVAL_MS);
    // When the user closes the tab or backgrounds the browser on mobile, send
    // any pending events immediately. In theory this also works on navigations,
    // but empirically we have not seen "visibilitychange" or "pagehide" fire in
    // that scenario. http://go/scrcast/NTY0OTQ5NjkxNTU3NDc4NHxjMzNkNDk2My1lZQ
    var onVisibilityChange = function() {
        if (document.visibilityState === "hidden") {
            flushEventsPoll(/* pageUnloading= */ true);
        }
    };
    var onPageHide = function() {
        flushEventsPoll(/* pageUnloading= */ true);
    };
    // Prefer Page Visibility API with a fallback to use pagehide.
    // http://go/mdn-win/pagehide_event#usage_notes
    if (typeof document.visibilityState !== "undefined") {
        window.addEventListener("visibilitychange", onVisibilityChange);
    } else {
        window.addEventListener("pagehide", onPageHide);
    }
    /** Report an event. Not flushed immediately. */ var report = function(category, type, payload) {
        queue.push(analytics.kaggle.analytics.ClientAnalyticsEvent.create({
            eventCategory: category,
            eventType: type,
            eventTime: new Date().toISOString(),
            eventDataJson: JSON.stringify(payload || {}),
            eventPage: getCurrentPage().pageType
        }));
    };
    return {
        report: report,
        flushEvents: function() {
            return flushEventsPoll(/* pageUnloading= */ true);
        },
        close: function() {
            cancelPoll();
            window.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
        }
    };
};
var AnalyticsToken = function AnalyticsToken(value) {
    "use strict";
    _classCallCheck(this, AnalyticsToken);
    this.value = value;
    this.refreshTime = new Date().getTime();
};
// This is shared with "Kaggle.Web/Views/Shared/Analytics/AnalyticsReporter.ts".
var ANALYTICS_TOKEN_KEY = "analyticsToken";
var getAnalyticsTokenFromLocalStorage = function() {
    try {
        var tokenJson = localStorage.getItem(ANALYTICS_TOKEN_KEY);
        if (!tokenJson) {
            return null;
        }
        var storedToken = JSON.parse(tokenJson);
        return storedToken.value;
    } catch (e) {
        return null;
    }
};
var saveAnalyticsTokenToLocalStorage = function(token) {
    try {
        localStorage.setItem(ANALYTICS_TOKEN_KEY, JSON.stringify(new AnalyticsToken(token)));
    } catch (e) {
    // no-op
    }
};
var getAnalyticsToken = function() {
    var _ref = _asyncToGenerator(function() {
        var response;
        return __generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    if (USE_STUB) {
                        return [
                            2,
                            "DEV_TOKEN"
                        ];
                    }
                    return [
                        4,
                        usersClient.accountService.generateAnalyticsToken({})
                    ];
                case 1:
                    response = _state.sent();
                    return [
                        2,
                        response.token
                    ];
            }
        });
    });
    return function getAnalyticsToken() {
        return _ref.apply(this, arguments);
    };
}();
// TODO(cochamberlain): Change filename from makeLogger.ts to logger.ts
/** Singleton logger, ensures only one instance exists. */ export var logger = createLogger(USE_STUB ? consoleFlushEventQueue : prodFlushEventQueue, getAnalyticsToken);
export var reportEvent = logger.report;
